import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../store";
import { setGlobalState } from "../../store/auth";
import { useQuery } from "@tanstack/react-query";
import { lookupQueries } from "@surgia/api/src/queryStore/shared/lookup";

// components
import Button from "@surgia/ui/components/button";
import TableControls from "@surgia/ui/containers/table/table-controls";
import GenericModal from "@surgia/ui/components/genericModal";
import { GenericTableHeader } from "@surgia/ui";
import HeaderLayout from "@surgia/ui/layouts/Header";
//containers
import BranchesTable from "../../containers/tables/branches-table";
import AssignBranchForm from "@surgia/ui/containers/assignBranchForm";

//hooks
import { useGetBranches } from "../../hooks/useGetBranches";
import { useSignIn } from "../../hooks/useSignIn";
import { useQueryClient } from "@tanstack/react-query";

//icons
import addIcon from "@surgia/ui/icons/actions/add.svg";
import { FiltersModal } from "../Users/index";
import { useOutletContext } from "react-router-dom";
// constants
const ITEMS_PER_PAGE = 8;

import { permissions } from "../../permissions";

import { checkUserPermissions } from "@surgia/utils";
export const Branches = () => {
  const { i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const authState = useSelector((state: RootState) => state.auth);
  const dispatch = useDispatch();
  const tenant_id = JSON.parse(localStorage.getItem("tenant_id") || "");
  const { handleSignIn } = useSignIn();
  const queryClient = useQueryClient();

  const [currentPage, setCurrentPage] = useState(0);
  const [openFilters, setOpenFilters] = useState(false);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({
    city_id: "",
  });
  const [showCreateBranchModal, setShowCreateBranchModal] = useState(false);

  // Add sorting state with initial sort by ID descending (newest first)
  const [sortState, setSortState] = useState<{
    column: string | null;
    direction: "asc" | "desc" | null;
  }>({
    column: "id", // Initial sort column
    direction: "desc", // Initial sort direction (desc for newest first)
  });

  interface OutletContextType {
    selectedBranch: string | null;
    setSelectedBranch: (branch: string | null) => void;
    branchesDropdown: any[];
    isbranchesDropDownLoading: boolean;
  }
  const {
    selectedBranch,
    setSelectedBranch,
    branchesDropdown,
    isbranchesDropDownLoading,
  } = useOutletContext<OutletContextType>();

  // Fetch cities data
  const { data: citiesResponse } = useQuery({
    ...lookupQueries.queries.cities({
      size: 1000,
      country_id: 191,
    }),
  });

  const cities = citiesResponse?.data || [];

  const {
    data,
    isLoading: isBranchesLoading,
    error,
    isError,
  } = useGetBranches({
    tenant_id: tenant_id,
    component_id: import.meta.env.VITE_PUBLIC_COMPONENT_ID,
    name: search,
    size: ITEMS_PER_PAGE,
    page: currentPage,
    branches_ids: selectedBranch?.includes(",")
      ? (authState.globalState.user_branches as string)
      : (selectedBranch ?? ""),
    // Add city filter
    city_id: filters.city_id || "",
    // Add sorting parameters
    sort:
      sortState.column && sortState.direction
        ? `${sortState.column},${sortState.direction}`
        : undefined,
  });
  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: ["branches"] });
  }, [authState.globalState.user_branches]);
  const { branches, total } = data || {};
  const handleAddBranch = () => {
    setShowCreateBranchModal(true);
  };
  useEffect(() => {
    setCurrentPage(0);
  }, [
    search,
    filters,
    selectedBranch?.includes(",")
      ? (authState.globalState.user_branches as string)
      : (selectedBranch ?? ""),
  ]);
  const handleSuccessfullyCreatedBranch = async () => {
    const authResult = await handleSignIn();
    setShowCreateBranchModal(false);

    // Get the new branch IDs from the authentication result
    let newBranchIds;
    if (authResult?.userInfo?.user_info?.user_branches) {
      newBranchIds = authResult.userInfo.user_info.user_branches.map(
        (branch: any) => branch.id
      );
    }

    // Create a proper payload that matches the setGlobalState action requirements
    const updatedGlobalState = {
      first_name: authState.globalState.first_name || "",
      last_name: authState.globalState.last_name || "",
      full_name: authState.globalState.full_name || "",
      email: authState.globalState.email || "",
      mobile: authState.globalState.mobile,
      subject_id: authState.globalState.subject_id || "",
      creation_date: authState.globalState.creation_date,
      last_accessed_branch_id: authState.globalState.last_accessed_branch_id,
      user_groups: authState.globalState.user_groups || [],
      user_permissions: authState.globalState.user_permissions || [],
      last_login: authState.globalState.last_login || "",
      user_branches: newBranchIds.join(","),
      access_token: authState.globalState.access_token || "",
      id_token: authState.globalState.id_token || "",
      refresh_token: authState.globalState.refresh_token || "",
      expires_at: authState.globalState.expires_at || 0,
      tenant_id: authState.globalState.tenant_id || 0,
    };

    dispatch(setGlobalState(updatedGlobalState));
  };

  // Handle sort changes
  const handleSort = (newSortState: {
    column: string | null;
    direction: "asc" | "desc" | null;
  }) => {
    setSortState(newSortState);
    setCurrentPage(0); // Reset to first page when sorting changes
  };

  if (isError) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h3 className="text-lg font-medium text-red-600 mb-2">
            Error loading branches
          </h3>
          <p className="text-gray-500">{error?.message}</p>
        </div>
      </div>
    );
  }
  return (
    <div dir={isRTL ? "rtl" : "ltr"}>
      <HeaderLayout
        title="Branches"
        subtitle="Manage and monitor all branches"
        additionalStyles="justify-between"
        branches={branchesDropdown}
        selectedBranch={selectedBranch}
        setSelectedBranch={setSelectedBranch}
        showBranchesDropDown={authState.globalState.user_branches?.includes(
          ","
        )}
        allUserBranches={authState.globalState.user_branches || ""}
        isBranchesLoading={isbranchesDropDownLoading}
      >
        <Button
          variant="primary"
          startIcon={addIcon}
          onClick={handleAddBranch}
          className={`${!checkUserPermissions([permissions.ADD_BRANCHES]) ? "pointer-events-none opacity-50 cursor-not-allowed" : ""}`}
        >
          Add Branch
        </Button>
      </HeaderLayout>
      <div className="bg-white rounded-xl border border-gray-200 shadow pt-4">
        <GenericTableHeader
          title="Branches"
          totalCount={total || 0}
          showBadge={true}
          customBadgeText={
            total != undefined
              ? `${total} ${total === 1 ? "branch" : "branches"}`
              : "0 branches"
          }
          layout="justified"
          borderStyle="gray-100"
          padding="px-4"
        >
          <TableControls
            statusTabs={[]}
            status=""
            setStatus={() => {}}
            search={search}
            setSearch={setSearch}
            openFilters={openFilters}
            setOpenFilters={setOpenFilters}
            showFilter={true}
          />
        </GenericTableHeader>

        {/* Branches Table Section */}
        <BranchesTable
          data={branches || []}
          currentPage={currentPage}
          totalPages={Math.ceil(total / ITEMS_PER_PAGE)}
          onPageChange={setCurrentPage}
          loading={isBranchesLoading}
          hasRequiredEditPermission={checkUserPermissions([
            permissions.EDIT_BRANCHES,
          ])}
          sortState={sortState}
          onSort={handleSort}
        />
      </div>

      {showCreateBranchModal && (
        <GenericModal
          title={"Add branch"}
          open={showCreateBranchModal}
          onClose={() => setShowCreateBranchModal(false)}
          footer={undefined}
        >
          <AssignBranchForm
            createBranch={true}
            createTenantBranch={true}
            onSuccess={handleSuccessfullyCreatedBranch}
            requestId={tenant_id}
            onClose={() => setShowCreateBranchModal(false)}
          />{" "}
        </GenericModal>
      )}

      {openFilters && (
        <FiltersModal
          open={openFilters}
          onClose={() => setOpenFilters(false)}
          onApply={() => setOpenFilters(false)}
          filters={filters}
          setFilters={setFilters}
          cities={cities}
          showCities={true}
          branches={[]}
        />
      )}
    </div>
  );
};

export default Branches;
